import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {SALE_DIRECTION} from '../../entity/sale.model';
import {
    FormArray,
    FormControl, FormGroup,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators
} from '@angular/forms';
import {SelectElement} from '../../../../../components/common/select/select.model';
import {Moment} from 'moment';
import {AccountingAccountModel} from '../../../../accounting/account/api/entity/accountingAccount.model';
import {MovementType} from '../../../../accounting/movement/api/entity/accountingMovement.model';
import {Subscription} from 'rxjs';
import {ValueAndLabelModel} from '../../../../common/model/valueAndLabel.model';
import {PartnerSummaryModel} from '../../../../partner/common/api/model/partnerSummary.model';

@Component({
    selector: 'app-sale-save-payment-requests-form',
    templateUrl: './sale-save-payment-requests-form.component.html',
})
export class SaleSavePaymentRequestsFormComponent implements OnInit, OnChanges, OnDestroy {

    @Input() responsableUserId: number;
    @Input() saleTotalPriceVatIncluded: number;
    @Input() saleDate: Moment;
    @Input() totalDepositAccordingToProducts: number;
    @Input() directionValue: SALE_DIRECTION;
    @Input() parentFormGroup: UntypedFormGroup;
    @Input() parentFormControlName: string;
    @Input() enableNoPaymentRequest = true;
    @Input() partner: PartnerSummaryModel;
    @Input() availableThirdPaymentTypes: SaleSavePaymentRequestsFormThirdPaymentType[] = [];

    @Output() totalAmountRequestedChanged = new EventEmitter<number>();

    paymentRequestFormArray: UntypedFormArray;
    typeFormControl: UntypedFormControl;
    errorFormControl: UntypedFormControl;

    selectedAccount: AccountingAccountModel;

    paymentTypeSelectElements: SelectElement[] = [];
    DIRECTION_IN = SALE_DIRECTION.SALE_DIRECTION_IN;
    MovementType = MovementType;
    subscriptions: Subscription[] = [];

    constructor(private formBuilder: UntypedFormBuilder) {
        this.paymentRequestFormArray = new UntypedFormArray([]);
        this.typeFormControl = new UntypedFormControl(undefined, Validators.required);
        this.errorFormControl = new UntypedFormControl(undefined, Validators.required);


        this.subscriptions.push(this.typeFormControl.valueChanges.subscribe(() => this.refreshPaymentRequests()));
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ((changes['parentFormGroup'] || changes['parentFormControlName']) && this.parentFormControlName && this.parentFormGroup) {
            const formGroup = this.parentFormGroup.get(this.parentFormControlName) as UntypedFormGroup;
            formGroup.addControl('paymentRequests', this.paymentRequestFormArray);
            formGroup.addControl('type', this.typeFormControl);
            formGroup.addControl('error', this.errorFormControl);
            this.subscriptions.push(formGroup.valueChanges.subscribe(() => {
                this.onTotalAmountRequestedChanged();
            }));
        }

        if (changes['totalDepositAccordingToProducts'] || changes['enableNoPaymentRequest']) {
            this.paymentTypeSelectElements = [];
            if (this.enableNoPaymentRequest === true) {
                this.paymentTypeSelectElements.push(new SelectElement('NONE', 'Aucune'));
            }
            this.paymentTypeSelectElements.push(new SelectElement('ONE', 'Paiement de la totalité du prix'));
            if (this.totalDepositAccordingToProducts > 0) {
                this.paymentTypeSelectElements.push(new SelectElement('DEPOSIT', 'Un paiement pour l\'acompte et un paiement pour le solde restant'));
                this.paymentTypeSelectElements.push(new SelectElement('CUSTOM', 'Personnalisé'));
            }
        }

        if (changes['saleTotalPriceVatIncluded'] || changes['totalDepositAccordingToProducts']) {
            this.typeFormControl.setValue(this.totalDepositAccordingToProducts > 0 ? 'DEPOSIT' : 'ONE');
        }

    }

    onTotalAmountRequestedChanged() {
        this.totalAmountRequestedChanged.emit(this.paymentRequestsTotalAmount);
    }

    get isPaymentRequestsAmountWrong(): boolean {
        return this.differenceBetweenTotalPriceVatIncludedAndPaymentRequestsTotalAmount !== 0 &&
            this.directionValue !== SALE_DIRECTION.SALE_DIRECTION_IN;
    }

    get differenceBetweenTotalPriceVatIncludedAndPaymentRequestsTotalAmount(): number {
        return this.roundNumber(this.saleTotalPriceVatIncluded - this.paymentRequestsTotalAmount);
    }

    get paymentRequestsTotalAmount(): number {
        let amountSum = 0;
        this.paymentRequestFormArray.controls.forEach(control => amountSum += (control as UntypedFormGroup).controls.amount.value);
        return this.roundNumber(amountSum);
    }

    get paymentWrongTotalAmountErrorMessage(): string {
        const diff = this.differenceBetweenTotalPriceVatIncludedAndPaymentRequestsTotalAmount;
        let message;
        if (diff > 0) {
            message = 'Les demandes de paiements ne couvrent pas le prix total de ';
        } else {
            message = 'Les demandes de paiements dépasse le prix total de ';
        }

        message += Math.abs(diff) + ' €';
        return message;
    }

    get paymentTypeFormControlValue(): string {
        return this.typeFormControl.value;
    }

    ngOnInit() {
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(value => value.unsubscribe());
    }

    refreshPaymentRequests() {
        const paymentType = this.paymentTypeFormControlValue;
        this.paymentRequestFormArray.clear();
        if (paymentType === 'ONE') {
            this.paymentRequestFormArray.push(this.newPaymentRequest(this.saleTotalPriceVatIncluded, 'Paiement de la totalité', true));
        } else if (paymentType === 'DEPOSIT' || paymentType === 'CUSTOM') {
            const deposit = this.totalDepositAccordingToProducts;
            this.paymentRequestFormArray.push(this.newPaymentRequest(deposit, 'Paiement de l\'acompte', true));
            const remaining = this.roundNumber(this.saleTotalPriceVatIncluded - deposit);
            if (paymentType === 'CUSTOM') {
                this.paymentRequestFormArray.push(this.newPaymentRequest(remaining, 'Paiement personnalisé', false));
            } else {
                this.paymentRequestFormArray.push(this.newPaymentRequest(remaining, 'Paiement du solde restant', false));
            }
        }
        this.amountChanged();
    }

    newPaymentRequest(providedAmount: number, label: string, collect: boolean): UntypedFormGroup {
        let amount = providedAmount + 0;
        if (!amount) {
            amount = this.roundNumber(this.differenceBetweenTotalPriceVatIncludedAndPaymentRequestsTotalAmount);
        }


        const amountFormControl = new UntypedFormControl(amount, Validators.required);
        const formGroup: UntypedFormGroup = this.formBuilder.group({
            label: new UntypedFormControl(label),
            amount: amountFormControl,
            deadline: new UntypedFormControl(undefined),
            onlinePayment: new UntypedFormControl(false),
            collect: new UntypedFormControl(collect),
            payer: new UntypedFormControl('partner'),
            thirdPartyPaymentType: new UntypedFormControl('none'),
            thirdPartyPartnerId: new UntypedFormControl(undefined),
            possibleAccountType: new UntypedFormControl(undefined)
        });
        this.paymentRequestCollectChanged(collect, formGroup);
        this.refreshPaymentRequestCommunication(formGroup);
        this.refreshPossibleAccountTypes(formGroup);

        this.subscriptions.push(amountFormControl.valueChanges.subscribe(() => {
            this.amountChanged();
        }));
        return formGroup;
    }

    amountChanged() {
        if (this.isPaymentRequestsAmountWrong === true) {
            this.errorFormControl.setValue(undefined);
        } else {
            this.errorFormControl.setValue(false);
        }
    }

    thirdPartyPaymentTypeChanged(itemFormGroup: FormGroup) {
        const type = itemFormGroup.get('thirdPartyPaymentType').value;
        itemFormGroup.patchValue({
            thirdPartyPartnerId: this.availableThirdPaymentTypes.find(value => value.value === type)?.partnerSummary?.id
        });
    }

    newPaymentRequestFromArrayForm = (): UntypedFormGroup => {
        this.onTotalAmountRequestedChanged();
        return this.newPaymentRequest(undefined, 'Paiement personnalisé', false);
    }

    paymentRequestCollectChanged(checked: boolean, paymentRequestFormGroup: UntypedFormGroup) {
        paymentRequestFormGroup.removeControl('accountingMovement');
        if (checked) {
            paymentRequestFormGroup.addControl('accountingMovement',
                new UntypedFormGroup({
                    accountId: new UntypedFormControl(undefined, Validators.required),
                    paymentTypeValue: new UntypedFormControl('CARD', Validators.required),
                    date: new UntypedFormControl(this.saleDate)
                }));
        }
        this.refreshPaymentRequestCommunication(paymentRequestFormGroup);
        this.refreshPossibleAccountTypes(paymentRequestFormGroup);
    }

    refreshPaymentRequestCommunication(paymentRequestFormGroup: UntypedFormGroup) {
        paymentRequestFormGroup.removeControl('generateCommunication');
        if (this.directionValue === SALE_DIRECTION.SALE_DIRECTION_OUT) {
            const collect = paymentRequestFormGroup.value.collect;
            if (!collect) {
                paymentRequestFormGroup.addControl('generateCommunication', new FormControl(false));
            }
        }
    }

    accountChanged(account: AccountingAccountModel) {
        this.selectedAccount = account;
    }

    paymentTypeChanged(paymentType: ValueAndLabelModel, itemFormGroup: UntypedFormGroup) {
        const control: FormGroup = itemFormGroup.get('accountingMovement') as FormGroup;
        if (control) {
            control.removeControl('check');
            if (paymentType?.value === 'CHECK') {
                control.addControl('check',
                    this.formBuilder.group({
                        number: this.formBuilder.control(undefined),
                        from: this.formBuilder.control(this.partner?.name),
                        bankValue: this.formBuilder.control(undefined),
                    }));
            }
        }
        this.refreshPossibleAccountTypes(itemFormGroup);
    }

    private roundNumber(nb: number): number {
        return Math.round(nb * 100) / 100;
    }

    refreshPossibleAccountTypes(itemFormGroup: UntypedFormGroup) {
        let possibleAccountType = undefined;
        console.log('refreshPossibleAccountTypes', itemFormGroup.get('accountingMovement.paymentTypeValue')?.value, itemFormGroup.value);
        const paymentTypeValue = itemFormGroup.get('accountingMovement.paymentTypeValue')?.value;
        if (paymentTypeValue === 'CHECK' || paymentTypeValue === 'CASH') {
            possibleAccountType = 'CHECKOUT';
        }
        if (paymentTypeValue === 'CARD' || paymentTypeValue === 'BANK') {
            possibleAccountType = 'BANK_ACCOUNT';
        }

        itemFormGroup.patchValue({
            possibleAccountType
        })
    }

    selectDefaultAccount(accountingMovementFormGroup: UntypedFormGroup, accounts: AccountingAccountModel[]) {
        if (this.responsableUserId) {
            const userAccount = accounts.find((account: AccountingAccountModel) => account.user?.id === this.responsableUserId);
            if (userAccount) {
                accountingMovementFormGroup.patchValue({
                    accountId: userAccount.id,
                });
            }
        }
    }
}

export class SaleSavePaymentRequestsFormThirdPaymentType {
    value: string;
    partnerSummary?: PartnerSummaryModel;
    label: string;
}
